import { Luach } from '@app/luach'
import { useQuery } from '@tanstack/react-query'
import { RotateCw } from 'lucide-react'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTRPC } from '../../lib/trpc'
import { Notice } from '../../ui'
import { LineLogic } from './LineLogic'

/**
 * The one part of the platform opened all the way up.
 *
 * ## Why this is live rather than a picture
 *
 * Everything here is computed by the real engine for a real congregation, on
 * request. A screenshot would be a second copy of the product and the copy is
 * always the one that goes stale; worse, this page's argument is *correctness*,
 * so illustrating it with something that cannot be checked would undercut the
 * only claim that matters. A visitor can open the shul's own public pages and
 * find the same times.
 *
 * ## What it has to prove, and how each control proves it
 *
 * The exhibit's thesis is the platform's, narrowed to one worked case: state a
 * thing once and everything downstream reads it.
 *
 * - **Pick a line** → the rule behind it: what it hangs off, the anchor's own
 *   resolved time, what gates it onto this sheet, and why the shul times it so.
 *   This is the "said once" half.
 * - **Pick a year** → the sheet recomputes, and lines move because the year
 *   moved them. Nothing was authored per year. This is the "and it keeps
 *   holding" half, which is the one nobody believes from prose.
 *
 * The years are not chosen by us — see `chipsFrom`.
 */
export function LuachExhibit() {
  const trpc = useTRPC()
  /*
   * The shul this page exhibits — named by the platform, not found by searching.
   *
   * What it requires is a MAINTAINED congregation whose times a visitor can go
   * and check at its own public address: the exhibit's force is that the sheet
   * here and the sheet there agree, and a thin sample tenant claims less than
   * the product does. Standing in the directory is a different question and not
   * one the exhibit asks — the demo shul is cloned nightly from a live
   * congregation, so its times are as real as that shul's while it belongs in
   * no list of places somebody could go and daven.
   *
   * Nothing in the model tells a maintained shul from a thin one, so the choice
   * is stated (`platform.featured_space_id`) and a second shul joining cannot
   * change what the front door shows.
   */
  const featured = useQuery(trpc.org.featured.queryOptions())
  const shul = featured.data ?? undefined

  // Every way each chag can fall — the engine scans until it has seen every
  // kevia, so the pool the chips are drawn from is the complete set rather than
  // whatever a window happened to reach.
  const patterns = useQuery({
    ...trpc.occasion.fallPatterns.queryOptions({
      spaceId: shul?.spaceId ?? '',
    }),
    enabled: !!shul,
  })

  // Today, as a plain ISO date. Only used to drop years already behind us, so
  // the shul's own zone would change the answer at most for a few hours around
  // midnight — and never for a chag months away.
  const today = new Date().toISOString().slice(0, 10)
  // Which draw of chips is showing. Zero is the nearest five; Refresh moves it.
  const [draw, setDraw] = useState(0)
  const chips = useMemo(
    () => chipsFrom(patterns.data ?? [], today, draw),
    [patterns.data, today, draw],
  )
  const [pickedChip, setPickedChip] = useState<string | null>(null)
  const chip = chips.find((c) => c.id === pickedChip) ?? chips[0]

  const sheet = useQuery({
    ...trpc.occasion.resolve.queryOptions({
      spaceId: shul?.spaceId ?? '',
      occasionKey: chip?.occasionKey ?? '',
      from: chip?.startDate,
    }),
    enabled: !!shul && !!chip,
  })

  const [pickedLine, setPickedLine] = useState<string | null>(null)
  const entries = (sheet.data?.days ?? []).flatMap((d) =>
    d.entries.map((e) => ({ entry: e, day: d })),
  )
  /*
   * Which line opens by default.
   *
   * It has to be one that actually demonstrates something, because it is the
   * first thing a visitor reads. That means a real offset off a zman WITH a
   * reason attached: "40 minutes before shkia, because…".
   *
   * Requiring a non-zero offset is the part that matters. Some rules exist only
   * to put a zman on the sheet, so they are stored as an offset of zero from
   * themselves — and `<Formula>` renders that honestly as "0 min after Sof Zman
   * Achilas Chametz", which is true, circular, and the worst possible opening
   * example. It stays clickable; it just does not lead.
   */
  const worthOpening = (e: (typeof entries)[number]['entry']) =>
    !!e.rationale &&
    e.formula.kind === 'offset' &&
    !!e.formula.anchor &&
    (e.formula.offsetMinutes ?? 0) > 0
  const chosen =
    entries.find((x) => x.entry.key === pickedLine) ??
    entries.find((x) => worthOpening(x.entry)) ??
    entries.find((x) => x.entry.rationale && x.entry.formula.anchor) ??
    entries[0]

  /*
   * A new sheet starts at the top.
   *
   * The scroll region keeps whatever offset it had, so moving from a short chag
   * to a long one left it parked near the bottom — the visitor picked Pesach and
   * landed on the footer of the last day.
   *
   * Keyed on the chip rather than on the selected line. An earlier version hunted
   * the pre-opened row and scrolled it into view, which is a nice idea and the
   * wrong one: where it lands depends on which line the sheet happens to explain
   * first, so the same click put the reader in a different place on every chag.
   * A sheet opening at its own beginning is predictable, and the first thing worth
   * explaining is near the top anyway.
   */
  const sheetRef = useRef<HTMLDivElement>(null)
  const chipId = chip?.id
  useEffect(() => {
    // `chipId` is read rather than merely listed: a dependency the body ignores is
    // one the lint rule calls unnecessary, correctly — it cannot tell a trigger
    // from a value. Guarding on it makes the intent true instead of suppressed.
    if (!chipId || !sheetRef.current) return
    sheetRef.current.scrollTop = 0
  }, [chipId])

  if (featured.isLoading || patterns.isLoading) {
    return <Notice>Computing this year’s schedule…</Notice>
  }
  if (!shul) {
    return <Notice>No congregation is publishing yet.</Notice>
  }
  if (patterns.isError || sheet.isError) {
    return (
      <Notice tone="error">
        The schedule engine did not answer. Nothing here is cached, so there is
        nothing to show in its place.
      </Notice>
    )
  }

  return (
    <div className="mk-ex">
      {/*
       * The years, as the calendar's own answer to "when is this awkward".
       * Rarity is the ordering, so the first chips are the years a gabbai would
       * warn you about — and the label says WHY each one is unusual, since a
       * bare year means nothing to a reader.
       */}
      <div className="mk-ex__pick">
        <div className="mk-ex__years" role="group" aria-label="Choose a year">
          {chips.map((c) => (
            <button
              type="button"
              key={c.id}
              className="mk-ex__year"
              data-active={c.id === chip?.id ? '' : undefined}
              onClick={() => {
                setPickedChip(c.id)
                // The new sheet has different lines; keeping the old selection
                // would silently show a rule that is not on the sheet in view.
                setPickedLine(null)
              }}
            >
              {/* The occasion leads. Four chips read "5787" in a row otherwise,
                and a year on its own names nothing a reader is choosing between
                — what they are picking is a chag in an awkward year. */}
              <span className="mk-ex__year-occ">{c.labelEn}</span>
              <span className="mk-ex__year-what">
                <span className="mk-ex__year-num">{c.hebrewYear}</span>
                {` · ${c.label}`}
              </span>
            </button>
          ))}
        </div>
        <button
          type="button"
          className="mk-ex__refresh"
          onClick={() => {
            // A different five out of the whole set, and the sheet follows the
            // chip that leads it — so the selection cannot outlive the chips it
            // was made from.
            setDraw((d) => d + 1)
            setPickedChip(null)
            setPickedLine(null)
          }}
          disabled={patterns.isFetching || sheet.isFetching}
        >
          <RotateCw className="mk-ex__refresh-icon" aria-hidden />
          {patterns.isFetching || sheet.isFetching ? 'Computing…' : 'Refresh'}
        </button>
      </div>

      <div className="mk-ex__body">
        <div className="mk-ex__sheet" ref={sheetRef}>
          {sheet.data ? (
            <div className="luach-frame">
              <Luach
                result={sheet.data}
                shul={shul}
                options={{
                  showSubtitle: true,
                  showCivilDate: true,
                  columns: 1,
                }}
                onEntryClick={(e) => setPickedLine(e.key)}
                selectedKey={chosen?.entry.key ?? null}
              />
            </div>
          ) : (
            <Notice>Computing…</Notice>
          )}
        </div>

        <div className="mk-ex__logic">
          {chosen ? (
            <LineLogic entry={chosen.entry} day={chosen.day} />
          ) : (
            <p className="mk-ex__hint">
              Pick a line to see the rule behind it.
            </p>
          )}
        </div>
      </div>
    </div>
  )
}

type Chip = {
  id: string
  occasionKey: string
  labelEn: string
  hebrewYear: number
  startDate: string
  label: string
}

/**
 * Turn fall-patterns into the years worth offering.
 *
 * Nothing here is a list of interesting years — that list would rot, and it
 * would be our opinion rather than the calendar's. Instead a pattern earns a
 * chip by being RARE: if a chag falls this way in a minority of the years
 * scanned, that is the year somebody has to be told about. The common shape gets
 * one chip at the end so a reader has something ordinary to compare against.
 *
 * The consequence worth stating: as the calendar gains flags, the grouping gets
 * finer and the chips get better, with no edit here. That is the same reason the
 * engine returns rarity rather than judging it.
 */
function chipsFrom(
  data: Array<{
    occasionKey: string
    labelEn: string
    yearsScanned: number
    patterns: Array<{
      weekday: string
      flags: string[]
      hebrewYears: number[]
      startDates: string[]
    }>
  }>,
  today: string,
  seed: number,
): Chip[] {
  const out: Chip[] = []

  for (const occ of data) {
    for (const p of occ.patterns) {
      // A pattern needs a reason a reader can recognize. Rarity alone offered
      // "Sukkos starts Tuesday", which is true, genuinely unusual, and says
      // nothing to anybody — an unlabeled chip is a year nobody clicks. So the
      // gate is a NAMED condition on day one, which is also exactly what makes
      // the sheet differ, and the label is that condition.
      const why = describe(p.flags)
      if (!why) continue

      // The soonest occurrence still ahead of us. Offering a year whose chag has
      // already passed invites the reader to check a sheet for a date behind
      // them, and the claim being made is about years to come.
      const i = p.startDates.findIndex((d) => d >= today)
      if (i === -1) continue

      out.push({
        id: `${occ.occasionKey}-${p.hebrewYears[i]}`,
        occasionKey: occ.occasionKey,
        labelEn: occ.labelEn,
        hebrewYear: p.hebrewYears[i],
        startDate: p.startDates[i],
        label: why,
      })
    }
  }

  // Chronological — the list reads as the calendar coming towards you rather
  // than as a menu of features, and the nearest awkward year leads.
  out.sort((a, b) => a.startDate.localeCompare(b.startDate))

  // Five, because six wrapped one chip onto a line of its own at desktop and a
  // single stranded chip reads as an afterthought rather than as the last of a
  // set.
  const SHOWN = 5
  // First paint is the nearest five, so the exhibit opens on the years a visitor
  // could actually be planning for. Refresh draws a different five from the
  // whole set — the set is every way these chagim can fall, so there is genuinely
  // more to see, and a button that recomputed the same five taught that Refresh
  // does nothing.
  if (seed === 0) return out.slice(0, SHOWN)

  const rand = mulberry32(seed)
  const pool = [...out]
  const picked: Chip[] = []
  while (picked.length < SHOWN && pool.length)
    picked.push(...pool.splice(Math.floor(rand() * pool.length), 1))
  return picked.sort((a, b) => a.startDate.localeCompare(b.startDate))
}

/**
 * A seeded PRNG, so a given refresh renders the same five every time React
 * re-runs the memo. `Math.random` inside a render is a different set on every
 * commit, which makes the chips flicker under any unrelated state change.
 */
function mulberry32(seed: number) {
  let a = seed >>> 0
  return () => {
    a = (a + 0x6d2b79f5) >>> 0
    let t = Math.imul(a ^ (a >>> 15), 1 | a)
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296
  }
}

/**
 * Why a year is unusual, in words a gabbai would use — or null when the only
 * thing unusual is the weekday, which is not worth a chip.
 *
 * The phrasing is deliberately occasion-agnostic ("falls on Shabbos" rather than
 * "Pesach on Shabbos"): the chip already carries the name, so repeating it there
 * would print the occasion twice on one control.
 *
 * The flags dropped first are the ones every chag carries; they say "this is Yom
 * Tov", which the reader already knows from its name.
 */
function describe(flags: string[]): string | null {
  // Read before Sunday is dropped, because for these two it is the whole point.
  // A chag whose day one follows Shabbos had EREV on Shabbos — erev Pesach on
  // Shabbos is the year a shul rebuilds its sheet by hand.
  if (flags.includes('Sunday') && flags.includes('Erev Yom Tov'))
    return 'erev is Shabbos'
  // A fast pushed off Shabbos and one that genuinely falls on Sunday both start
  // as Shabbos goes out and are not the same day, so each says which it is.
  if (flags.includes("Tisha b'Av Nidche")) return 'pushed to Sunday'
  if (flags.includes("Shabbos erev Tisha b'Av"))
    return 'begins as Shabbos goes out'

  const notable = flags.filter(
    (f) =>
      f !== 'Yom Tov' &&
      f !== 'Erev Yom Tov' &&
      f !== 'Motzei Yom Tov' &&
      f !== 'Sunday',
  )
  if (notable.includes('Eruv Tavshilin')) return 'runs into Shabbos'
  if (notable.includes('Shabbos')) return `falls on Shabbos`
  // The flag is about the day ITSELF being Friday, so the phrase says that.
  if (notable.includes('Erev Shabbos')) return `falls on erev Shabbos`
  return null
}
